import React, { useState, useRef, useEffect } from "react"
import { Box, Button, Container, Typography, useTheme } from "@mui/material"

import { BatBox, BatBoxHandle, FillingBox, PageTitle, SnowBox } from "../components/StyledElements"
import { useThemeContext } from "../services/ThemeContext"
import { styled } from "@mui/material/styles"

import { ReactComponent as BoxedLights } from "../assets/svg/boxed-lights.svg"

export const ThemeMenu = (): React.ReactElement => {
  const { setTheme, themesShown, mode: themeMode, flash, setDarken } = useThemeContext()
  const theme = useTheme()
  const [showSnow, setShowSnow] = useState(false)
  const snowTimer = useRef<number | null>(null)
  const batBoxRef = useRef<BatBoxHandle>(null)

  const spawnBats = (n: number) => {
    for (let i = 0; i < n; i++) {
      batBoxRef.current?.spawn()
    }
  }

  useEffect(() => {
    return () => {
      if (snowTimer.current) {
        clearTimeout(snowTimer.current)
        snowTimer.current = null
      }
    }
  }, [])

  return (
    <>
      <PageTitle>{"Thème"}</PageTitle>
      <Container maxWidth="lg" sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", py: 8 }}>
        {themesShown.some((theme) => theme.name === "default") && (
          <ThemeButton
            variant="contained"
            sx={{
              backgroundColor: "#0C315E",
              color: "#fff",
            }}
            onClick={() => setTheme("default")}
          >
            <ShadeBox />
            <ThemeButtonText>{"Classique"}</ThemeButtonText>
          </ThemeButton>
        )}
        {themesShown.some((theme) => theme.name === "light") && (
          <ThemeButton
            variant="contained"
            sx={{
              backgroundColor: "#FFFFFF",
              color: "#000",
              "&:hover": { boxShadow: "0px 0px 30px white" },
              "&:hover .text": { color: "#666" },
            }}
            onClick={() => {
              setTheme("light")
              if (themeMode !== "light") flash()
            }}
          >
            <ThemeButtonText className="text" sx={{ transition: "color 0.3s" }}>
              {"Mode jour"}
            </ThemeButtonText>
          </ThemeButton>
        )}
        {themesShown.some((theme) => theme.name === "ultraDark") && (
          <ThemeButton
            variant="contained"
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": { boxShadow: "0px 0px 30px black" },
              "&:hover .text": { color: "#666" },
            }}
            onClick={() => {
              setTheme("ultraDark")
            }}
            onMouseEnter={() => setDarken(true)}
            onMouseLeave={() => setDarken(false)}
          >
            <ThemeButtonText className="text" sx={{ transition: "color 0.3s" }}>
              {"Ultra sombre"}
            </ThemeButtonText>
          </ThemeButton>
        )}
        {themesShown.some((theme) => theme.name === "glass") && (
          <ThemeButton
            variant="contained"
            sx={{
              backgroundColor: "#88888833",
              boxShadow: "0px 0px 3px #88888888",
              "&:hover": { boxShadow: "0px 0px 3px #88888888" },
            }}
            onClick={() => {
              setTheme("glass")
            }}
          >
            <ShineBarsBox />
            <ThemeButtonText className="text" sx={{ transition: "color 0.3s" }}>
              {"Verre"}
            </ThemeButtonText>
          </ThemeButton>
        )}
        {themesShown.some((theme) => theme.name === "christmas") && (
          <ThemeButton
            variant="contained"
            sx={{
              backgroundColor: "#5e1a0c",
              color: "#fff",
              "&:hover .snowflake": { opacity: 0.7 },
              ...(theme.name === "christmas"
                ? {}
                : {
                    "&:not(:hover) .red-light": { animation: "none" },
                    "&:not(:hover) .green-light": { animation: "none" },
                    "&:not(:hover) .blue-light": { animation: "none" },
                  }),
            }}
            onClick={() => setTheme("christmas")}
            onMouseEnter={() => {
              if (snowTimer.current) {
                clearTimeout(snowTimer.current)
                snowTimer.current = null
              }
              setShowSnow(true)
            }}
            onMouseLeave={() => {
              if (snowTimer.current) clearTimeout(snowTimer.current)
              snowTimer.current = window.setTimeout(() => {
                setShowSnow(false)
                snowTimer.current = null
              }, 300)
            }}
          >
            <ShadeBox />
            {showSnow && <SnowBox n={20} baseOpacity={0} />}
            <BoxedLights style={{ position: "absolute", top: 0, left: 0, width: "100%", zIndex: 3 }} />
            <ThemeButtonText>{"Noël"}</ThemeButtonText>
          </ThemeButton>
        )}
        {themesShown.some((theme) => theme.name === "halloween") && (
          <ThemeButton
            variant="contained"
            sx={{
              backgroundColor: "#63360c",
              color: "#fff",
            }}
            onClick={() => {
              ;(setTheme("halloween"), spawnBats(3))
            }}
            onMouseEnter={() => spawnBats(1)}
            onMouseLeave={() => spawnBats(1)}
          >
            <ShadeBox />
            <BatBox ref={batBoxRef} defaultFilghtTime={0.5} />
            <ThemeButtonText>{"Halloween"}</ThemeButtonText>
          </ThemeButton>
        )}
        {themesShown.some((theme) => theme.name === "classic") && (
          <ThemeButton
            variant="contained"
            disableRipple
            sx={{
              backgroundColor: "#9D9D9D",
              color: "#000",
              transition: "0s",
              "&:hover": { backgroundColor: "#757575" },
              "&:active": { backgroundColor: "#5E5E5E" },
            }}
            onClick={() => setTheme("classic")}
          >
            <ThemeButtonText sx={{ fontFamily: "Times, Times New Roman, serif" }}>{"Rétro"}</ThemeButtonText>
          </ThemeButton>
        )}
      </Container>
    </>
  )
}

const ThemeButton = styled(Button)({
  width: "300px",
  height: "150px",
  padding: "16px",
  margin: "16px",
  position: "relative",
  overflow: "hidden",
  transition: "box-shadow 0.3s",
  "&:hover .slide": { transform: "translate(0px, 0px)" },
  "&:hover .shade": { opacity: 0.2 },
})

const ThemeButtonText = styled(Typography)({
  fontSize: "1.5rem",
  textTransform: "none",
  position: "relative",
  zIndex: 3,
})

const ShadeBox = (): React.ReactElement => {
  return (
    <FillingBox
      className="shade"
      sx={{
        backgroundColor: "black",
        maskImage: "linear-gradient(to bottom, transparent, black)",
        opacity: 0,
        transition: "opacity 0.3s",
        zIndex: 1,
      }}
    />
  )
}

const ShineBarsBox = (): React.ReactElement => {
  return (
    <FillingBox
      className="slide"
      sx={{
        transform: "translate(-300px)",
        transition: "transform 0.7s cubic-bezier(0.3, 0, 0.3, 1)",
        zIndex: 2,
      }}
    >
      <Box
        className="shine"
        sx={{
          height: "300px",
          width: "10px",
          transform: "translateX(50px) rotate(45deg)",
          backgroundColor: "white",
          opacity: 0.2,
          position: "absolute",
        }}
      />
      <Box
        className="shine"
        sx={{
          height: "300px",
          width: "30px",
          transform: "rotate(45deg)",
          backgroundColor: "white",
          opacity: 0.2,
          position: "absolute",
        }}
      />
    </FillingBox>
  )
}
